import Button from '@/gf/components/ButtonOld'
import Layout from '@/gf/components/Layout'
import Modal from '@/gf/components/Modal'
import Page from '@/gf/components/Page'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import GraphQL from '@/gf/modules/GraphQL'
import UserContext from '@/gf/modules/User'
import { CREATE_SHIPPING_LABEL } from '@/gf/mutations/shipping'
import * as api from '@/suppliers/modules/api'
import { StoreOrdersSearch } from '@/suppliers/modules/api'
import { useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { GroundShipmentPayload, ShipmentLabel } from '../../types'
import Frame from '../components/Frame'
import useSession from '../hooks/useSession'
import ConfigureShipmentForm from './ConfigureShipmentForm'

const ConfigureShipment = () => {
  const { storeOrderId } = useParams<{ storeOrderId: string }>() as { storeOrderId: string }

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'Orders', href: '/orders' },
      { name: 'Order details', href: `/orders/${storeOrderId}` },
      { name: 'Create shipment', href: `/orders/${storeOrderId}/configure-shipment` },
    ],
  }

  const navigate = useNavigate()
  const { user, store } = useSession()
  const [_msgs, msgsMgr] = useMsgs()
  const [storeOrder, setStoreOrder] = useState<StoreOrdersSearch['storeOrders'][number] | null>()
  const [spinnerLive, spinner] = useToggle()
  const [labelResponse, setLabelResponse] = useState<ShipmentLabel>()
  const [printGroundLabelModalOpen, printGroundLabelModalToggler] = useToggle(false)
  const [createShippingLabel] = useMutation(CREATE_SHIPPING_LABEL)

  useEffect(() => {
    api.fetchStoreOrder(storeOrderId).then((so) => {
      if (so && !so.gearflowShipping) window.location.href = `/orders/${storeOrderId}/shipping`
      setStoreOrder(so)
    })
  }, [storeOrderId])

  // Return a loading page when the storeOrder is undefined
  if (storeOrder === undefined) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Loading..." />
      </Frame>
    )
  }

  // Not found or not authorized
  // TODO: move auth check to backend
  if (!storeOrder || (!UserContext.isAdmin(user) && storeOrder.store.id !== store.id)) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>Cannot find that order.</p>
              <p>
                <Link className="text-blue-500 underline" to="/orders">
                  Back to all orders
                </Link>
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    )
  }

  const onSubmitGround = (groundShipmentPayload: GroundShipmentPayload) => {
    spinner.on()
    createShippingLabel({
      variables: {
        ...groundShipmentPayload,
        shipToAddress: GraphQL.cleanGraphQlInput(groundShipmentPayload.shipToAddress),
        shipFromAddress: GraphQL.cleanGraphQlInput(groundShipmentPayload.shipFromAddress),
      },
    })
      .then((response) => {
        spinner.off()
        if (response.errors) {
          msgsMgr.add('Whoops, something went wrong. Please contact support.', 'negative')
        } else {
          setLabelResponse(response.data.createShippingLabel)
          printGroundLabelModalToggler.on()
        }
      })
      .catch((error) => {
        spinner.off()
        if (error.message === 'timeout') {
          msgsMgr.add(
            'Our shipping label provider is having trouble. Please submit again. If this problem persists, please contact Gearflow support.',
            'negative'
          )
        } else if (error.message) {
          msgsMgr.add(error.message, 'negative')
        } else {
          msgsMgr.add('Whoops, something went wrong. Please contact support.', 'negative')
        }
      })
  }

  const onSubmitLtl = () => {
    msgsMgr.add('LTL shipping is not available at this time.', 'negative')
    // TODO: LTL stubbed for future implementation
  }

  return (
    <Frame breadcrumbs={breadcrumbs}>
      {!['PARTIALLY_SHIPPED', 'PROCESSING'].includes(storeOrder.state) && (
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>
                Orders can only be shipped when they are in the &apos;processing&apos; state. This
                order is in the &apos;{storeOrder.state}&apos; state.
              </p>
              <p>
                Please return to the{' '}
                <Link className="text-blue-500 underline" to={`/orders/${storeOrderId}`}>
                  order details page
                </Link>
                .
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      )}
      {!storeOrder.gearflowShipping && (
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>Only orders using Gearflow shipping are able to access this page.</p>
              <p>
                Please return to the{' '}
                <Link className="text-blue-500 underline" to={`/orders/${storeOrderId}`}>
                  order details page
                </Link>
                .
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      )}
      {['PARTIALLY_SHIPPED', 'PROCESSING'].includes(storeOrder.state) &&
        storeOrder.gearflowShipping && (
          <Page title="Create shipment" previousPage={`/orders/${storeOrderId}`}>
            {/* Print label modal */}
            <Modal
              title="Success"
              open={printGroundLabelModalOpen}
              onClose={() => {
                printGroundLabelModalToggler.off()
                navigate(`/orders/${storeOrderId}`)
              }}
              cancelText="Done"
              footerAction={
                labelResponse && (
                  <a href={labelResponse.url} target="_blank" rel="noreferrer">
                    <Button title="Print label" />
                  </a>
                )
              }
            >
              <p className="text-sm text-gray-700">
                Print your shipping label now or later from the order page.
              </p>
            </Modal>
            <ConfigureShipmentForm
              storeOrder={storeOrder}
              spinnerLive={spinnerLive}
              onSubmitGround={onSubmitGround}
              onSubmitLtl={onSubmitLtl}
            />
          </Page>
        )}
    </Frame>
  )
}

export default ConfigureShipment
